import { useEffect, useState, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Loader2, ArrowLeft, Lock, Users, ShieldCheck, Save } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LEVEL_STYLES = {
    none: { label: 'Aucun', color: 'bg-red-100 text-red-700 border-red-200 hover:bg-red-200' },
    read: { label: 'Lecture', color: 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200' },
    write: { label: 'Écriture', color: 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200' },
};
const LEVEL_ORDER = ['none', 'read', 'write'];

const FLAG_LABELS = {
    restrict_to_own_commercial_id: 'Limiter aux données de ce commercial (filtrage par commercial_id)',
    lock_devis_after_envoye: 'Verrouiller un devis en lecture seule après envoi',
    receive_notifications: 'Recevoir les notifications',
};

const ROLE_LABELS = { admin: 'Administrateur', commercial: 'Commercial', assistante: 'Assistante' };

function LevelPill({ value, onClick, disabled }) {
    const cfg = LEVEL_STYLES[value] || LEVEL_STYLES.none;
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors min-w-[78px] text-center ${cfg.color} ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
            data-testid={`level-pill-${value}`}
        >
            {cfg.label}
        </button>
    );
}

function cyclLevel(current) {
    const i = LEVEL_ORDER.indexOf(current || 'none');
    return LEVEL_ORDER[(i + 1) % LEVEL_ORDER.length];
}

export default function AdminPermissionsPage() {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [catalog, setCatalog] = useState(null);
    const [users, setUsers] = useState([]);
    const [roleDefaults, setRoleDefaults] = useState([]);
    const [loading, setLoading] = useState(true);

    // Dialog user perms
    const [editUser, setEditUser] = useState(null);
    const [draftPerms, setDraftPerms] = useState({});
    const [draftFlags, setDraftFlags] = useState({});
    const [saving, setSaving] = useState(false);

    // Dialog role defaults
    const [editRole, setEditRole] = useState(null);
    const [draftRolePerms, setDraftRolePerms] = useState({});
    const [draftRoleFlags, setDraftRoleFlags] = useState({});

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [c, u, r] = await Promise.all([
                axios.get(`${API}/api/admin/permissions/catalog`, { withCredentials: true }),
                axios.get(`${API}/api/admin/permissions/users`, { withCredentials: true }),
                axios.get(`${API}/api/admin/permissions/role-defaults`, { withCredentials: true }),
            ]);
            setCatalog(c.data);
            setUsers(u.data);
            setRoleDefaults(r.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const modulesBySection = useMemo(() => {
        if (!catalog) return {};
        const grouped = {};
        for (const s of catalog.sections) grouped[s.key] = [];
        for (const m of catalog.modules) (grouped[m.section] ||= []).push(m);
        return grouped;
    }, [catalog]);

    if (user && user.role !== 'admin') return <Navigate to="/" replace />;

    // --- USER PERMS DIALOG ---
    const openUserDialog = (u) => {
        setEditUser(u);
        setDraftPerms({ ...u.effective_perms });
        setDraftFlags({ ...u.effective_flags });
    };

    const saveUserPerms = async () => {
        if (!editUser) return;
        setSaving(true);
        try {
            await axios.put(
                `${API}/api/admin/permissions/users/${editUser.id}`,
                { permissions: draftPerms, flags: draftFlags },
                { withCredentials: true }
            );
            toast.success('Permissions enregistrées');
            setEditUser(null);
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };

    // --- ROLE DEFAULTS DIALOG ---
    const openRoleDialog = (role) => {
        const rd = roleDefaults.find(r => r.role === role);
        if (!rd) return;
        setEditRole(role);
        setDraftRolePerms({ ...rd.permissions });
        setDraftRoleFlags({ ...rd.flags });
    };

    const saveRoleDefaults = async () => {
        if (!editRole) return;
        setSaving(true);
        try {
            await axios.put(
                `${API}/api/admin/permissions/role-defaults/${editRole}`,
                { permissions: draftRolePerms, flags: draftRoleFlags },
                { withCredentials: true }
            );
            toast.success(`Défauts du rôle « ${ROLE_LABELS[editRole]} » enregistrés`);
            setEditRole(null);
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="admin-permissions-page">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={() => navigate('/admin')} data-testid="perms-back-btn">
                        <ArrowLeft size={16} className="mr-1.5" />
                        Retour
                    </Button>
                    <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-purple-100">
                            <Lock size={20} className="text-purple-600" />
                        </div>
                        <div>
                            <h1 className="text-xl md:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Permissions</h1>
                            <p className="text-xs text-slate-500">Gérer les accès par utilisateur et par rôle</p>
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#2e7cd6]" /></div>
            ) : (
                <Tabs defaultValue="users" className="space-y-4" data-testid="perms-tabs">
                    <TabsList>
                        <TabsTrigger value="users" data-testid="tab-users">
                            <Users size={14} className="mr-1.5" /> Par utilisateur
                        </TabsTrigger>
                        <TabsTrigger value="roles" data-testid="tab-roles">
                            <ShieldCheck size={14} className="mr-1.5" /> Défauts par rôle
                        </TabsTrigger>
                    </TabsList>

                    {/* --- ONGLET UTILISATEURS --- */}
                    <TabsContent value="users" className="space-y-3">
                        {users.map(u => (
                            <Card key={u.id} className="border-slate-200" data-testid={`user-perm-card-${u.id}`}>
                                <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                                    <div className="flex items-center gap-3 flex-1 min-w-[220px]">
                                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm" style={{ backgroundColor: u.couleur || '#94a3b8' }}>
                                            {(u.name || '?').charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-slate-800">{u.name}</p>
                                            <p className="text-xs text-slate-500">{u.email}</p>
                                        </div>
                                    </div>
                                    <Badge variant="outline" className="text-xs">{ROLE_LABELS[u.role] || u.role}</Badge>
                                    {!u.is_active && <Badge variant="outline" className="text-xs text-red-600">Désactivé</Badge>}
                                    <Button size="sm" variant="outline" onClick={() => openUserDialog(u)} data-testid={`edit-perms-${u.id}`} disabled={u.role === 'admin'}>
                                        <Lock size={13} className="mr-1.5" />
                                        {u.role === 'admin' ? 'Accès complet' : 'Configurer'}
                                    </Button>
                                </CardContent>
                            </Card>
                        ))}
                    </TabsContent>

                    {/* --- ONGLET DÉFAUTS PAR RÔLE --- */}
                    <TabsContent value="roles" className="space-y-3">
                        {roleDefaults.map(rd => (
                            <Card key={rd.role} className="border-slate-200" data-testid={`role-card-${rd.role}`}>
                                <CardHeader className="pb-2 border-b border-slate-100">
                                    <CardTitle className="text-sm font-semibold flex items-center justify-between gap-2">
                                        <span className="flex items-center gap-2">
                                            <ShieldCheck size={16} className="text-[#2e7cd6]" />
                                            {ROLE_LABELS[rd.role] || rd.role}
                                        </span>
                                        <Button size="sm" variant="outline" onClick={() => openRoleDialog(rd.role)} disabled={rd.role === 'admin'} data-testid={`edit-role-${rd.role}`}>
                                            <Lock size={13} className="mr-1.5" />
                                            {rd.role === 'admin' ? 'Accès complet (non modifiable)' : 'Modifier les défauts'}
                                        </Button>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="pt-3 text-xs text-slate-600">
                                    <p>Flags : {Object.entries(rd.flags).map(([k, v]) => `${FLAG_LABELS[k] || k} : ${v ? 'Oui' : 'Non'}`).join(' · ')}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </TabsContent>
                </Tabs>
            )}

            {/* --- Dialog : permissions utilisateur --- */}
            <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
                <DialogContent className="max-w-4xl max-h-[90dvh] overflow-hidden flex flex-col p-0" data-testid="user-perms-dialog">
                    <DialogHeader className="px-5 py-3 border-b">
                        <DialogTitle>Permissions — {editUser?.name} <span className="text-xs font-normal text-slate-500">({ROLE_LABELS[editUser?.role]})</span></DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
                        {/* Flags */}
                        <Card className="border-slate-200">
                            <CardHeader className="pb-2 border-b border-slate-100">
                                <CardTitle className="text-sm font-semibold">Comportement spécial</CardTitle>
                            </CardHeader>
                            <CardContent className="pt-3 space-y-2">
                                {Object.keys(FLAG_LABELS).map(k => (
                                    <label key={k} className="flex items-center justify-between gap-3 py-1.5 cursor-pointer">
                                        <span className="text-sm text-slate-700">{FLAG_LABELS[k]}</span>
                                        <Switch checked={!!draftFlags[k]} onCheckedChange={(v) => setDraftFlags(p => ({ ...p, [k]: v }))} data-testid={`flag-${k}`} />
                                    </label>
                                ))}
                            </CardContent>
                        </Card>

                        {/* Modules groupés par section */}
                        {catalog?.sections.map(section => (
                            <Card key={section.key} className="border-slate-200">
                                <CardHeader className="pb-2 border-b border-slate-100 bg-slate-50">
                                    <CardTitle className="text-sm font-semibold text-slate-700">{section.label}</CardTitle>
                                </CardHeader>
                                <CardContent className="pt-3 p-0">
                                    <div className="divide-y divide-slate-100">
                                        {(modulesBySection[section.key] || []).map(m => (
                                            <div key={m.key} className="flex items-center justify-between gap-3 px-4 py-2">
                                                <span className="text-sm text-slate-700">{m.label}</span>
                                                <LevelPill
                                                    value={draftPerms[m.key] || 'none'}
                                                    onClick={() => setDraftPerms(p => ({ ...p, [m.key]: cyclLevel(p[m.key]) }))}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                    <DialogFooter className="px-5 py-3 border-t gap-2">
                        <div className="flex gap-2 ml-auto">
                            <Button variant="outline" size="sm" onClick={() => setEditUser(null)} disabled={saving}>Annuler</Button>
                            <Button size="sm" className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white" onClick={saveUserPerms} disabled={saving} data-testid="save-user-perms">
                                {saving ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Save size={13} className="mr-1.5" />} Enregistrer
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* --- Dialog : défauts d'un rôle --- */}
            <Dialog open={!!editRole} onOpenChange={(o) => !o && setEditRole(null)}>
                <DialogContent className="max-w-4xl max-h-[90dvh] overflow-hidden flex flex-col p-0" data-testid="role-perms-dialog">
                    <DialogHeader className="px-5 py-3 border-b">
                        <DialogTitle>Défauts du rôle — {ROLE_LABELS[editRole]}</DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
                        <Card className="border-slate-200">
                            <CardHeader className="pb-2 border-b border-slate-100">
                                <CardTitle className="text-sm font-semibold">Comportement spécial</CardTitle>
                            </CardHeader>
                            <CardContent className="pt-3 space-y-2">
                                {Object.keys(FLAG_LABELS).map(k => (
                                    <label key={k} className="flex items-center justify-between gap-3 py-1.5 cursor-pointer">
                                        <span className="text-sm text-slate-700">{FLAG_LABELS[k]}</span>
                                        <Switch checked={!!draftRoleFlags[k]} onCheckedChange={(v) => setDraftRoleFlags(p => ({ ...p, [k]: v }))} />
                                    </label>
                                ))}
                            </CardContent>
                        </Card>
                        {catalog?.sections.map(section => (
                            <Card key={section.key} className="border-slate-200">
                                <CardHeader className="pb-2 border-b border-slate-100 bg-slate-50">
                                    <CardTitle className="text-sm font-semibold text-slate-700">{section.label}</CardTitle>
                                </CardHeader>
                                <CardContent className="pt-3 p-0">
                                    <div className="divide-y divide-slate-100">
                                        {(modulesBySection[section.key] || []).map(m => (
                                            <div key={m.key} className="flex items-center justify-between gap-3 px-4 py-2">
                                                <span className="text-sm text-slate-700">{m.label}</span>
                                                <LevelPill
                                                    value={draftRolePerms[m.key] || 'none'}
                                                    onClick={() => setDraftRolePerms(p => ({ ...p, [m.key]: cyclLevel(p[m.key]) }))}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                    <DialogFooter className="px-5 py-3 border-t">
                        <Button variant="outline" size="sm" onClick={() => setEditRole(null)} disabled={saving}>Annuler</Button>
                        <Button size="sm" className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white" onClick={saveRoleDefaults} disabled={saving} data-testid="save-role-defaults">
                            {saving ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Save size={13} className="mr-1.5" />} Enregistrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
